import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { DeselectSelectedUsers, RemoveSearchAllUsers, SelectFilteredUsers, ToggleSelectedUser } from 'app/library/all-users/actions';
import { FilterByDialogComponent } from 'app/library/all-users/components/dialog/filter-by-dialog/filter-by-dialog.component';
import { SelectedFilterGroupVm } from 'app/library/all-users/components/selected-filters-chip/selected-filter-chip.vm';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { AudienceUserExtended } from 'app/library/audience-user/models/audience-user.extended.model';
import { AudienceUser } from 'app/library/audience-user/models/audience-user.model';
import { DeselectSelectedAudienceUsers, SearchAudienceUsers, SelectFilteredAudienceUsers, ToggleSelectedAudienceUser } from 'app/library/audience-users/actions';
import { IManageAudienceUserActionPayload } from 'app/library/audience-users/interfaces/audience-user-action.action-payload.interface';
import { IManageAudienceUsersActionPayload } from 'app/library/audience-users/interfaces/audience-users-action.action-payload.interface';
import { AudienceUsersState } from 'app/library/audience-users/models/audience-users.state';
import { AudienceUsersService } from 'app/library/audience-users/services/audience-users.service';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { AudienceExperienceStatusEnum } from 'app/library/audience/enums/audience-experience-status.enum';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { GrowMoveAudienceMembersDialogComponent } from 'app/library/grow/dialogs/grow-move-audience-members-dialog/grow-move-audience-members-dialog.component';
import { IAudienceFilters } from 'app/library/grow/interfaces/audience-filters.interface';
import { isAutomaticAudience } from 'app/library/grow/utils/audience-display.util';
import {
    audienceRulesToChoosenFilters,
    buildAudienceFilterSummaryGroups,
    choosenFiltersToAudiencePayload,
    enrichAudienceFilterRulesWithLabels,
    extractAudienceFilterUnassignedFlags,
    getAudienceFilterRuleValueId,
    IAudienceFilterEntityLookups,
    isExtendedAudienceFilterField,
} from 'app/library/grow/utils/audience-filter.mapper.util';
import { ExtendedAttributeCategoryEnum } from 'app/library/extended-attributes/enums/extended-attribute-category.enum';
import { ExtendedAttributeService } from 'app/library/extended-attributes/services/extended-attribute.service';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { ModuleService } from 'app/library/module/services/module.service';
import { Team } from 'app/library/team/models/team.model';
import { TeamRoleFilterOptions } from 'app/library/team/models/team-role-filter-options.model';
import { TeamService } from 'app/library/team/services/team.service';
import { UserRoleIdEnum } from 'app/projects/shared/src/lib/enums/user-role-id.enum';
import {
    AudienceMembersActionsService,
    IAudienceMembersActionsContext,
    IAudienceMembersActionsPermissions,
} from 'app/library/grow/services/audience-members-actions.service';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { AutocompleteDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/autocomplete/autocomplete.component';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { CreateUserSuccess } from 'app/projects/user/src/lib/actions/create-entity-success.action';
import { SearchUsers } from 'app/projects/user/src/lib/actions/search-entities.action';
import { User } from 'app/projects/user/src/lib/models/user';
import { UsersState } from 'app/projects/user/src/lib/models/users.state';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-audience-users',
    templateUrl: './audience-users.component.html',
    styleUrls: ['./audience-users.component.scss'],
})
export class AudienceUsersComponent extends UnsubscribeOnDestroy implements OnInit {
    private _isUnassignedSelected = new BehaviorSubject<boolean>(sessionStorage.getItem('isUnassignedSelected') === 'true');
    isUnassignedSelected$ = this._isUnassignedSelected.asObservable();

    private _loadUsers = new BehaviorSubject<boolean>(null);
    loadUser$ = this._loadUsers.asObservable();

    audienceId: string;
    audience: MaxBrainAudience;

    @Select(AudienceUsersState.getEntities)
    users$: Observable<AudienceUser[]>;

    @Select(AudienceUsersState.getEntitiesWithUser)
    filteredAudienceUsers$: Observable<AudienceUserExtended[]>;

    @Select(AudienceUsersState.getSelectedEntityIds)
    selectedUserIds$: Observable<string[]>;
    selectedUserIds: [{ id: string }];

    @Select(AudienceUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @Select(AudienceUsersState.getSearchTerm)
    searchTerm$: Observable<string>;

    searchInput = new FormControl(this._store.selectSnapshot(AudienceUsersState.getSearchTerm));

    @Select(AllUsersState.getSelectedEntityIds)
    selectedRootUserIds$: Observable<string[]>;
    selectedRootUserIds: [{ id: string }];

    @Select(AllUsersState.getEntities)
    filteredAllUsers$: Observable<User[]>;

    @Select(AllUsersState.getEntities)
    rootUsers$: Observable<User[]>;

    @Select(AllUsersState.getFilteredEntities)
    filteredRootAudienceUsers$: Observable<User[]>;

    @Select(AllUsersState.getTotalCount)
    rootTotalCount$: Observable<number>;

    @Select(UsersState.getSearchTerm)
    rootSearchTerm$: Observable<string>;

    rootSearchInput = new FormControl();

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    dropdownDialogRef: MatDialogRef<AutocompleteDialogComponent>;
    engageMoveUsersDialogRef: MatDialogRef<GrowMoveAudienceMembersDialogComponent>;
    filterByDialogRef: MatDialogRef<FilterByDialogComponent>;

    sortBy = this._store.selectSnapshot(AudienceUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AudienceUsersState.getSortOrder);

    usersUploadInProgress = false;
    isRootAudience: boolean;
    isAutomaticAudienceFlag = false;
    audienceFilters: IAudienceFilters | null = null;
    audienceFilterGroups: SelectedFilterGroupVm[] = [];
    filtersLoading = false;
    membersReloadToken = 0;
    private _audienceContextInitialized = false;
    private _cachedFilterEntityLookups: IAudienceFilterEntityLookups | null = null;
    private _cachedFilterEntityLookupsKey = '';

    actionPermissions: IAudienceMembersActionsPermissions;
    actionsUserCount = 0;
    private _currentTotalCount = 0;
    private _selectedMembershipIds: string[] = [];
    private _selectedRootIds: string[] = [];

    screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    @HostListener('window:resize')
    onResize() {
        this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    }

    constructor(
        private _audienceUsersService: AudienceUsersService,
        private _audienceService: AudienceService,
        private _experienceService: ExperienceService,
        private _teamService: TeamService,
        private _moduleService: ModuleService,
        private _extendedAttributeService: ExtendedAttributeService,
        private _allUsersApiService: AllUsersApiService,
        private _audienceMembersActionsService: AudienceMembersActionsService,
        private _translateService: TranslateService,
        private _route: ActivatedRoute,
        private _store: Store,
        private _action$: Actions,
        private _matDialog: MatDialog,
        private _location: Location,
        public sidenavService: SidenavService,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        private _cd: ChangeDetectorRef
    ) {
        super();
        this.actionPermissions = this._audienceMembersActionsService.getPermissions();
    }

    /**
     * On init
     */
    async ngOnInit(): Promise<void> {
        this.audienceId = this._route.parent.snapshot.params['id'];
        this._store.dispatch(new RemoveSearchAllUsers(true));
        this._audienceService.audiences$
            .pipe(
                filter((x) => !!x),
                map(() => this._audienceService.getAudienceById(this.audienceId)),
                filter((audience) => !!audience),
                distinctUntilChanged((previous, current) => this._isSameAudienceSnapshot(previous, current)),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(async (audience) => {
                await this._syncAudienceFromStore(audience);
            });

        this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(CreateUserSuccess)).subscribe(() => {
            this._loadUsers.next(false);
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchTerm) => {
            this._store.dispatch(new SearchAudienceUsers(searchTerm));
        });

        this.rootSearchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchTerm) => {
            this._store.dispatch(new SearchUsers(searchTerm));
        });

        this.selectedUserIds$
            .pipe(
                filter((selectedUserIds) => !!selectedUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedUserIds) => {
                this._selectedMembershipIds = selectedUserIds;
                this.selectedUserIds = selectedUserIds.map((id) => ({ id: id })) as [{ id: string }];
                this._updateActionsUserCount();
            });

        this.selectedRootUserIds$
            .pipe(
                filter((selectedRootUserIds) => !!selectedRootUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedRootUserIds) => {
                this._selectedRootIds = selectedRootUserIds;
                this.selectedRootUserIds = selectedRootUserIds.map((id) => ({ id: id })) as [{ id: string }];
                this._updateActionsUserCount();
            });

        this.totalCount$.pipe(takeUntil(this._unsubscribeAll)).subscribe((childTotal) => {
            if (!this.isRootAudience) {
                this._currentTotalCount = childTotal || 0;
                this._updateActionsUserCount();
            }
        });

        this.rootTotalCount$.pipe(takeUntil(this._unsubscribeAll)).subscribe((rootTotal) => {
            if (this.isRootAudience) {
                this._currentTotalCount = rootTotal || 0;
                this._updateActionsUserCount();
            }
        });
    }

    downloadUsers(): void {
        this._audienceMembersActionsService.downloadUsers(this._buildActionsContext());
    }

    openPasswordlessAccessDialog(): void {
        void this._audienceMembersActionsService.openPasswordlessAccessDialog(this._buildActionsContext());
    }

    openAssignUserToTeamDialog(): void {
        void this._audienceMembersActionsService.openAssignUserToTeamDialog(this._buildActionsContext());
    }

    assignUsersToExperience(): void {
        void this._audienceMembersActionsService.assignUsersToExperience(this._buildActionsContext());
    }

    assignUsersToAudience(): void {
        void this._audienceMembersActionsService.assignUsersToAudience(this._buildActionsContext());
    }

    assignUsersToCourse(): void {
        void this._audienceMembersActionsService.assignUsersToCourse(this._buildActionsContext());
    }

    resetPasswords(): void {
        void this._audienceMembersActionsService.resetPasswords(this._buildActionsContext());
    }

    removeMembersViaActions(): void {
        this._audienceMembersActionsService.removeUsers(this._buildActionsContext());
    }

    private _buildActionsContext(): IAudienceMembersActionsContext {
        return {
            audienceId: this.audienceId,
            audienceName: this.audience?.name || '',
            searchQuery: this.isRootAudience ? this.rootSearchInput.value : this.searchInput.value,
            selectedUserIds: this._getSelectedUserIdsForActions(),
            totalCount: this._currentTotalCount,
            onAfterAction: () => {
                this._store.dispatch(new DeselectSelectedAudienceUsers());
                this.deselect();
                this._loadUsers.next(true);
            },
        };
    }

    private _getSelectedUserIdsForActions(): string[] {
        if (this.isRootAudience) {
            return this._selectedRootIds;
        }

        const audienceUsers = this._audienceUsersService.getAudienceUsers() || [];
        return this._selectedMembershipIds
            .map((membershipId) => audienceUsers.find((audienceUser) => audienceUser.id === membershipId)?.userId)
            .filter((userId): userId is string => !!userId);
    }

    private _updateActionsUserCount(): void {
        const selectedCount = this.isRootAudience ? this._selectedRootIds.length : this._selectedMembershipIds.length;
        this.actionsUserCount = selectedCount || this._currentTotalCount;
        this._cd.markForCheck();
    }

    deselect(): void {
        this._store.dispatch(new DeselectSelectedUsers());
    }

    goBack() {
        this._location.back();
    }

    private _isSameAudienceSnapshot(previous: MaxBrainAudience, current: MaxBrainAudience): boolean {
        return (
            previous.id === current.id &&
            previous.name === current.name &&
            previous.isValid === current.isValid &&
            previous.error === current.error
        );
    }

    private async _syncAudienceFromStore(audience: MaxBrainAudience): Promise<void> {
        if (!audience) {
            if (!this._audienceContextInitialized) {
                this.isRootAudience = true;
                this._audienceContextInitialized = true;
            }

            return;
        }

        const shouldLoadFilters = !this._audienceContextInitialized && isAutomaticAudience(audience);

        this.audience = audience;
        this.isRootAudience = !audience.parent;
        this.isAutomaticAudienceFlag = isAutomaticAudience(audience);
        this._audienceContextInitialized = true;
        this._updateActionsUserCount();

        if (shouldLoadFilters) {
            await this.loadAudienceFilters();
        }

        this._cd.markForCheck();
    }

    get hasAudienceFilters(): boolean {
        return (this.audienceFilters?.rules ?? []).some((rule) => rule.operator === 'not_assigned' || (rule.values?.length ?? 0) > 0);
    }

    trackAudienceFilterGroup(_: number, group: SelectedFilterGroupVm): string {
        return group.groupKey;
    }

    private _getFilterRulesCacheKey(rules: IAudienceFilters['rules']): string {
        return JSON.stringify(rules ?? []);
    }

    private async _getFilterEntityLookups(rules: IAudienceFilters['rules']): Promise<IAudienceFilterEntityLookups> {
        const cacheKey = this._getFilterRulesCacheKey(rules);

        if (this._cachedFilterEntityLookups && this._cachedFilterEntityLookupsKey === cacheKey) {
            return this._cachedFilterEntityLookups;
        }

        const lookups = await this._buildAudienceFilterEntityLookups(rules);
        this._cachedFilterEntityLookups = lookups;
        this._cachedFilterEntityLookupsKey = cacheKey;

        return lookups;
    }

    private async _refreshAudienceFilterGroups(): Promise<void> {
        const rules = this.audienceFilters?.rules ?? [];
        const extendedAttributeLabels = await this._getExtendedAttributeLabels(rules);
        const lookups = await this._getFilterEntityLookups(rules);
        const enrichedRules = enrichAudienceFilterRulesWithLabels(rules, lookups);

        this.audienceFilterGroups = buildAudienceFilterSummaryGroups(
            enrichedRules,
            (key) => this._translateService.instant(key),
            extendedAttributeLabels
        );
    }

    private async _buildAudienceFilterEntityLookups(rules: IAudienceFilters['rules']): Promise<IAudienceFilterEntityLookups> {
        const lookups: IAudienceFilterEntityLookups = {
            userRoleById: {
                [UserRoleIdEnum.Admin]: this._translateService.instant('GENERAL.ADMINISTRATOR'),
                [UserRoleIdEnum.User]: this._translateService.instant('USER.SINGULAR'),
            },
            userStatusById: {
                active: this._translateService.instant('USER.STATUS.ACTIVE'),
                inactive: this._translateService.instant('USER.STATUS.INACTIVE'),
            },
            teamRoleById: Object.fromEntries(
                TeamRoleFilterOptions.createDefaultOptions().map((option) => [
                    option.id,
                    this._translateService.instant(option.translationKey),
                ])
            ),
        };

        if (rules.some((rule) => rule.field === 'experience')) {
            let experiences = this._experienceService.getExperiences();

            if (!experiences?.length) {
                await this._experienceService.fetchExperiences();
                experiences = this._experienceService.getExperiences();
            }

            lookups.experienceById = Object.fromEntries(
                (experiences ?? []).map((experience) => [String(experience.id), experience.name])
            );
        }

        if (rules.some((rule) => rule.field === 'team')) {
            let teams = this._teamService.getTeams();

            if (!teams?.length) {
                await this._teamService.fetchTeams();
                teams = this._teamService.getTeams();
            }

            lookups.teamById = Object.fromEntries(this._flattenTeams(teams ?? []).map((team) => [String(team.id), team.name]));
        }

        if (rules.some((rule) => rule.field === 'course')) {
            const courseIds = [
                ...new Set(
                    rules
                        .filter((rule) => rule.field === 'course' && rule.operator !== 'not_assigned')
                        .flatMap((rule) => rule.values.map((value) => getAudienceFilterRuleValueId(value)))
                        .filter(Boolean)
                ),
            ];

            const courseById: Record<string, string> = {};

            for (const courseId of courseIds) {
                const module = this._moduleService.getModuleById(courseId);

                if (module?.name) {
                    courseById[courseId] = module.name;
                }
            }

            lookups.courseById = courseById;
        }

        return lookups;
    }

    private _flattenTeams(teams: Team[]): Team[] {
        return teams.flatMap((team) => [team, ...this._flattenTeams(team.children ?? [])]);
    }

    private async _getExtendedAttributeLabels(rules: IAudienceFilters['rules']): Promise<Record<string, string>> {
        const extendedFields = rules.filter((rule) => isExtendedAudienceFilterField(rule.field)).map((rule) => rule.field);

        if (!extendedFields.length) {
            return {};
        }

        let attributes = this._extendedAttributeService.getExtendedAttributes();

        if (!attributes?.length) {
            await this._extendedAttributeService.fetchExtendedAttributes([ExtendedAttributeCategoryEnum.USER]);
            attributes = this._extendedAttributeService.getExtendedAttributes();
        }

        return attributes.reduce<Record<string, string>>((labels, attribute) => {
            labels[attribute.id] = attribute.translationKey;
            return labels;
        }, {});
    }

    async loadAudienceFilters(): Promise<void> {
        this.filtersLoading = true;

        try {
            this.audienceFilters = await this._audienceService.fetchAudienceFilters(this.audienceId);
            await this._refreshAudienceFilterGroups();
        } catch {
            this.audienceFilters = { rules: [] };
            this.audienceFilterGroups = [];
        } finally {
            this.filtersLoading = false;
        }
    }

    async openFilterDialog(): Promise<void> {
        const storageKey = `automaticAudienceFilters_${this.audienceId}`;
        const previousAllUsersFilters = this._allUsersApiService.getFiltersFromStorage('allUsersFiltersList');
        const rules = this.audienceFilters?.rules ?? [];
        const lookups = await this._getFilterEntityLookups(rules);
        const initialFilters = audienceRulesToChoosenFilters(enrichAudienceFilterRulesWithLabels(rules, lookups));
        const unassignedFlags = extractAudienceFilterUnassignedFlags(this.audienceFilters?.rules);

        this._allUsersApiService.setFiltersList(storageKey, initialFilters, true);
        this._allUsersApiService.setFiltersFromStorage(storageKey);

        this.filterByDialogRef = this._matDialog.open(FilterByDialogComponent, {
            disableClose: true,
            width: '1100px',
            panelClass: 'filter-by-overflow-dialog',
            data: {
                excludeAudienceFilter: true,
                filtersListStorageKey: storageKey,
                saveMode: 'automaticAudience',
                extendedAttributeCategory: ExtendedAttributeCategoryEnum.USER,
                isUnassignedModuleSelected: unassignedFlags.module,
                isUnassignedExperienceSelected: unassignedFlags.experience,
                isUnassignedTeamSelected: unassignedFlags.team,
            },
        });

        this.filterByDialogRef.afterClosed().subscribe(async (result) => {
            if (result?.saveMode !== 'automaticAudience') {
                this._allUsersApiService.setFiltersList('allUsersFiltersList', previousAllUsersFilters);
                this._allUsersApiService.setFiltersFromStorage('allUsersFiltersList');
                this.filterByDialogRef = null;
                return;
            }

            const filtersList = result.filtersList ?? [];
            const payload = choosenFiltersToAudiencePayload(filtersList, {
                module: result.isUnassignedModuleSelected,
                experience: result.isUnassignedExperienceSelected,
                team: result.isUnassignedTeamSelected,
            });

            try {
                await this._audienceService.updateAudienceFilters(this.audienceId, payload);
                this._cachedFilterEntityLookups = null;
                this._cachedFilterEntityLookupsKey = '';
                // PUT may return an empty rules payload when no members match; GET has the saved filters.
                this.audienceFilters = await this._audienceService.fetchAudienceFilters(this.audienceId);
                await this._refreshAudienceFilterGroups();
                this.membersReloadToken++;
                this._cd.markForCheck();
            } catch {
                this._allUsersApiService.setFiltersList('allUsersFiltersList', previousAllUsersFilters);
                this._allUsersApiService.setFiltersFromStorage('allUsersFiltersList');
            }

            this._allUsersApiService.setFiltersList('allUsersFiltersList', previousAllUsersFilters);
            this._allUsersApiService.setFiltersFromStorage('allUsersFiltersList');
            this.filterByDialogRef = null;
        });
    }

    /**
     * On selected change
     */
    selectUser(user: User): void {
        if (this.isRootAudience) {
            this._store.dispatch(new ToggleSelectedUser(user.id));
        } else {
            this._store.dispatch(new ToggleSelectedAudienceUser(user.id));
        }
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle(isAllSelected: boolean): void {
        if (this.isRootAudience) {
            this._store.dispatch(isAllSelected ? new DeselectSelectedUsers() : new SelectFilteredUsers());
        } else {
            this._store.dispatch(isAllSelected ? new DeselectSelectedAudienceUsers() : new SelectFilteredAudienceUsers());
        }
    }

    async openDialogAddUsers(): Promise<void> {
        const audienceRelevantExperiences = await this._audienceService.fetchAudienceRelevantExperiences(this.audienceId, AudienceExperienceStatusEnum.RELEVANT);

        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.filteredAllUsers$;
        dialogRef.componentInstance.entityId = this.audienceId;
        dialogRef.componentInstance.title = 'ENGAGE.BUTTONS.ADD_USERS';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'audienceMember';
        dialogRef.componentInstance.audienceRelevantExperiences = audienceRelevantExperiences;
        dialogRef.componentInstance.experienceIds = audienceRelevantExperiences.map((exp) => exp.experience.id);
        dialogRef.componentInstance.enforceExperienceFiltering = true;

        dialogRef.afterClosed().subscribe(async (result: { list: User[] }) => {
            if (result && result.list) {
                const payload: IManageAudienceUserActionPayload = {
                    users: result.list.map((user) => ({ id: user.id })) as [{ id: string }],
                    audiences: [{ id: this.audienceId }],
                };
                await this._audienceUsersService.addUsersToAudience(payload);
                // Fetch audiences to refresh list of audiences in a sidebar and show indicator on right place MAX-6698
                await this._audienceService.fetchAudiences();
                this._audienceService.getAudiences();
            }
        });
    }

    removeAudienceUsers(): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'ENGAGE.AUDIENCE.DIALOGS.REMOVE_USER.TITLE.PLURAL';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('ENGAGE.AUDIENCE.DIALOGS.REMOVE_USER.MESSAGE.PLURAL', {
            audienceName: this.audience.name,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const audienceUsers = this._audienceUsersService.getAudienceUsers() || [];
                const userIds = this.selectedUserIds
                    .map((selected) => audienceUsers.find((audienceUser) => audienceUser.id === selected.id)?.userId)
                    .filter((userId): userId is string => !!userId);

                await this._audienceUsersService.deleteAudienceUsers({ audienceId: this.audienceId, userIds });
                this.confirmDialogRef = null;
            }
        });
    }

    openDialogMoveUsers(): void {
        this.engageMoveUsersDialogRef = this._matDialog.open(GrowMoveAudienceMembersDialogComponent, {
            disableClose: false,
            width: '680px',
            autoFocus: false,
        });
        const audiences = this._audienceService.getAudiences();
        const audienceListTree = this._audienceService.convertListToTree(audiences);
        this.engageMoveUsersDialogRef.componentInstance.title = 'ENGAGE.AUDIENCE.DIALOGS.MOVE_USER.PLURAL';
        this.engageMoveUsersDialogRef.componentInstance.label = 'ENGAGE.AUDIENCE.DIALOGS.MOVE_USER.LABEL';
        this.engageMoveUsersDialogRef.componentInstance.entityId = this.audienceId;
        this.engageMoveUsersDialogRef.componentInstance.audienceListTree = audienceListTree[0];
        if (this.isRootAudience) {
            this.engageMoveUsersDialogRef.componentInstance.selectedUserId$ = this.selectedRootUserIds$;
        } else {
            this.engageMoveUsersDialogRef.componentInstance.selectedUserId$ = this.selectedUserIds$;
        }

        this.engageMoveUsersDialogRef.afterClosed().subscribe(async (result: IManageAudienceUsersActionPayload) => {
            if (result) {
                let responsePayload = undefined;
                if (this.audience.parent) {
                    responsePayload = await this._audienceUsersService.moveAudienceUsers(result.source, result.target, result.selectedUserIds);
                } else {
                    const payload: IManageAudienceUserActionPayload = {
                        users: result.selectedUserIds,
                        audiences: [{ id: result.target }],
                    };
                    responsePayload = await this._audienceUsersService.moveRootAudienceUsers(payload);
                }
                /*  const newlyAssignedUsersLength = responsePayload.newlyAssigned.length;
                const alreadyAssignedUsersLength = responsePayload.alreadyAssigned.length;
                const alreadyInChildrenUsersLength = responsePayload.alreadyInChildren.length;
                const successMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.MESSAGE.ASSIGN_USERS_SUCCESS', {
                    newlyAssignedUsersLength,
                    alreadyAssignedUsersLength,
                    alreadyInChildrenUsersLength
                });
                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.slow
                }); */
            }
        });
    }
}
